import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Alert,
  Grid,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Paper,
  Container,
  MenuItem,
  CircularProgress,
} from '@mui/material';
import StyledButton from '../../common/StyledButton';
import StyledTextField from '../../common/StyledTextField';
import StyledRadio from '../../common/StyledRadio';
import { categories as categoriesApi } from '../../../services/api';
import { formatCheckInDateLabel } from '../../../utils/festivalDates';
import { normalizeWebsite } from '../../../utils/website';

const VendorProfile = ({ onNext, initialData = {}, eventDate = null }) => {
  const [formData, setFormData] = useState({
    business_name: initialData.business_name || '',
    description: initialData.description || '',
    exhibit_type: initialData.exhibit_type || '',
    check_in_date: initialData.check_in_date || '',
    website: initialData.website || '',
    phone: initialData.phone || '',
    address: initialData.address || '',
    city: initialData.city || '',
    state: initialData.state || '',
    zip_code: initialData.zip_code || '',
    category_id: initialData.category_id ? String(initialData.category_id) : '',
    category_other_description: initialData.category_other_description || '',
  });
  const [error, setError] = useState('');
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await categoriesApi.list();
        if (!cancelled) {
          setCategoryOptions(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to load categories', err);
          setError('Could not load craft categories. Please refresh and try again.');
        }
      } finally {
        if (!cancelled) setCategoriesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const selectedCategory = useMemo(
    () => categoryOptions.find((c) => String(c.id) === String(formData.category_id)),
    [categoryOptions, formData.category_id]
  );
  const isOtherSelected = selectedCategory?.slug === 'other';

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      // Clear stale "Other" text when switching away so we don't accidentally
      // submit a description for a non-Other category.
      ...(name === 'category_id' ? { category_other_description: '' } : {}),
    }));
  };

  const handleWebsiteBlur = () => {
    if (!formData.website) return;
    const normalized = normalizeWebsite(formData.website);
    // null means parse failed — leave the original value so the user can fix
    // it. An empty string means trimmed-empty, and any string is the canonical
    // form we'd save. Show whichever the user will actually submit.
    if (typeof normalized === 'string' && normalized !== formData.website) {
      setFormData((prev) => ({ ...prev, website: normalized }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.category_id) {
      setError('Please choose a primary craft category.');
      return;
    }
    if (isOtherSelected && !formData.category_other_description.trim()) {
      setError('Please describe your craft (since you chose Other).');
      return;
    }

    let normalizedWebsite = '';
    if (formData.website) {
      normalizedWebsite = normalizeWebsite(formData.website);
      if (normalizedWebsite === null) {
        setError(
          "That website doesn't look right. Try something like www.yourshop.com or https://yourshop.com, or leave it blank."
        );
        return;
      }
    }

    try {
      const payload = {
        ...formData,
        website: normalizedWebsite,
        category_id: Number(formData.category_id),
        category_other_description: isOtherSelected
          ? formData.category_other_description.trim()
          : null,
      };
      onNext(payload);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Container maxWidth="md">
      <Paper elevation={0} sx={{ p: { xs: 2, md: 4 }, mt: 2, borderRadius: 2 }}>
        <Box component="form" onSubmit={handleSubmit}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          <Typography variant="h5" sx={{ mb: 4, fontWeight: 500 }}>
            Vendor Profile
          </Typography>

          <Grid container spacing={3}>
            {/* Business Information */}
            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Business Name"
                name="business_name"
                value={formData.business_name}
                onChange={handleChange}
                required
              />
            </Grid>

            <Grid item xs={12}>
              <StyledTextField
                select
                fullWidth
                label="Primary Craft Category"
                name="category_id"
                value={formData.category_id}
                onChange={handleChange}
                required
                disabled={categoriesLoading}
                helperText={
                  categoriesLoading
                    ? 'Loading categories…'
                    : selectedCategory?.description ||
                      'Pick the category that best describes your handmade craft. Choose "Other" if none fit.'
                }
                InputProps={{
                  endAdornment: categoriesLoading
                    ? <CircularProgress size={16} sx={{ mr: 2 }} />
                    : null,
                }}
              >
                {categoryOptions.map((cat) => (
                  <MenuItem key={cat.id} value={String(cat.id)}>
                    {cat.name}
                  </MenuItem>
                ))}
              </StyledTextField>
            </Grid>

            {isOtherSelected && (
              <Grid item xs={12}>
                <StyledTextField
                  fullWidth
                  label="Describe your craft"
                  name="category_other_description"
                  value={formData.category_other_description}
                  onChange={handleChange}
                  required
                  inputProps={{ maxLength: 120 }}
                  helperText="Short label since you chose Other (e.g. Resin Art, Leatherworking, Beekeeping). Max 120 characters."
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="Type of Exhibit"
                name="exhibit_type"
                value={formData.exhibit_type}
                onChange={handleChange}
                required
                multiline
                rows={2}
                inputProps={{ maxLength: 200 }}
                helperText="One short sentence for your booth listing (25 words or less). Example: Handmade soaps, candles, and body care products."
              />
            </Grid>

            <Grid item xs={12}>
              <StyledTextField
                fullWidth
                label="List and Describe Items You'll Be Selling"
                name="description"
                value={formData.description}
                onChange={handleChange}
                required
                multiline
                rows={4}
                placeholder="Handmade bar soap — ..."
                helperText="List each product type with a short description. Examples: Handmade bar soap — made with locally sourced ingredients and essential oils; Sourdough bread — weekly batches using organic flour from regional mills; Hand-thrown ceramic mugs — each piece is wheel-thrown and glazed by hand."
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                label="Website"
                name="website"
                type="text"
                inputMode="url"
                autoComplete="url"
                value={formData.website}
                onChange={handleChange}
                onBlur={handleWebsiteBlur}
                helperText="Optional. We'll add https:// for you. Example: www.yourshop.com"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <StyledTextField
                fullWidth
                label="Phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </Grid>

            {/* Address Information */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}>
                Address
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, backgroundColor: 'background.default' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <StyledTextField
                      fullWidth
                      label="Street Address"
                      name="address"
                      value={formData.address}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <StyledTextField
                      fullWidth
                      label="City"
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <StyledTextField
                      fullWidth
                      label="State"
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <StyledTextField
                      fullWidth
                      label="ZIP Code"
                      name="zip_code"
                      value={formData.zip_code}
                      onChange={handleChange}
                      required
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            {/* Check-in Date */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 500, color: 'text.secondary' }}>
                Check-in Date
              </Typography>
              <FormControl component="fieldset" sx={{ width: '100%' }} required>
                <RadioGroup
                  name="check_in_date"
                  value={formData.check_in_date}
                  onChange={handleChange}
                  row
                  sx={{ flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}
                >
                  <FormControlLabel 
                    value="friday" 
                    control={<StyledRadio />} 
                    label={formatCheckInDateLabel(eventDate, 'friday')} 
                  />
                  <FormControlLabel 
                    value="saturday" 
                    control={<StyledRadio />} 
                    label={formatCheckInDateLabel(eventDate, 'saturday')} 
                  />
                </RadioGroup>
              </FormControl>
            </Grid>
          </Grid>

          <Box sx={{ 
            mt: 4, 
            display: 'flex', 
            justifyContent: 'space-between',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2
          }}>
            <StyledButton 
              onClick={() => window.history.back()}
              fullWidth
              sx={{ maxWidth: { sm: '200px' } }}
            >
              Back
            </StyledButton>
            <StyledButton
              type="submit"
              variant="contained"
              fullWidth
              sx={{ maxWidth: { sm: '200px' } }}
            >
              Next
            </StyledButton>
          </Box>
        </Box>
      </Paper>
    </Container>
  );
};

export default VendorProfile; 